import { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

import { createUseDebounce } from "../../../lib/debounce";
import { LeadDetails } from "../../../lib/hooks/api/leads/useLead";
import { useLeadEntityActions } from "../../../lib/hooks/api/leads/useLeadEntityActions";
import { useRelateBidToLead } from "../../../lib/hooks/api/leads/useRelateBidToLead";
import { useUpdateLeads } from "../../../lib/hooks/api/leads/useUpdateLeads";
import { routePaths } from "../../../lib/routes";
import { diffArray } from "../../../lib/utils";
import Button from "../../ui/button/Button";

export const updateLeadSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullish(),
  value: z.coerce.number().nonnegative().nullish(),
  stageId: z.uuid(),
  assignedToId: z.uuid().nullish(),
  // transform the date object to a timestamp string
  expiryDate: z.coerce
    .date()
    .transform((date) => date.toISOString())
    .nullish(),
  buyerIds: z.uuid().array(),
  supplierIds: z.uuid().array(),
  frameworkId: z.uuid().nullish(),
  bidId: z.uuid().nullish(),
});

export type LeadUpdateFormInput = z.input<typeof updateLeadSchema>;
export type LeadUpdateFormOutput = z.output<typeof updateLeadSchema>;

// Define which fields are entity fields
const entityFieldsSchema = updateLeadSchema.pick({
  buyerIds: true,
  supplierIds: true,
  frameworkId: true,
});

type EntityField = keyof z.infer<typeof entityFieldsSchema>;

// Get the keys at runtime from the schema's shape
const entityFieldKeys = Object.keys(entityFieldsSchema.shape) as EntityField[];

const isEntityField = (name: string): name is EntityField =>
  entityFieldKeys.includes(name as EntityField);

interface UseLeadUpdateFormSubmitParams {
  lead: LeadDetails;
  form: UseFormReturn<LeadUpdateFormInput>;
}

/**
 * Manages form submission logic for lead updates, handling both direct field updates
 * and entity relationship changes (buyers, suppliers, frameworks).
 *
 * This hook separates two distinct update patterns:
 * 1. **Direct field updates** - Simple fields like name, status, etc. that can be
 *    patched directly via the updateLeads mutation
 * 2. **Entity field updates** - Relationship fields (buyers, suppliers, frameworks)
 *    that require add/remove operations to maintain many-to-many relationships
 *
 * Most fields are debounced and submitted on change, while the `value` field
 * is submitted on blur to avoid excessive API calls during numeric input.
 *
 * @example
 * ```tsx
 * const { debouncedHandleSubmit, submitValueUpdate } = useLeadUpdateFormSubmit({
 *   lead,
 *   form,
 * });
 *
 *  Use with useFormAutoSubmit for automatic change detection
 * useFormAutoSubmit(form, debouncedHandleSubmit);
 *
 *  Attach to value field's onBlur
 * <Input {...register("value")} onBlur={submitValueUpdate} />
 * ```
 */
export function useLeadUpdateFormSubmit({ lead, form }: UseLeadUpdateFormSubmitParams) {
  const {
    reset,
    trigger,
    getValues,
    formState: { dirtyFields },
  } = form;

  const { removeLeadEntities, addLeadEntities } = useLeadEntityActions({ leadId: lead.id });

  const { mutate: updateLeads } = useUpdateLeads({
    onSuccess: () => {
      toast.success("Lead updated successfully");
      // Reset dirty state while preserving current values
      // This prevents re-submission of already-saved changes
      reset({}, { keepValues: true });
    },
    onError: (error) => {
      console.warn("Failed to update lead", JSON.stringify(error));
      toast.error("Failed to update lead");
    },
  });

  /**
   * Relates a bid to a lead. This is a special case because a bidId is not on the lead table directly, it's a relationship that needs to be created/removed. So we use a separate mutation and handlers for this field.
   * On success, we redirect to the bid details page if a bid was related, or show a toast if the relationship was removed.
   */
  const { mutate: relateBidToLead } = useRelateBidToLead({
    onSuccess: (data) => {
      if (data.relateBidToLead.bid) {
        toast.success(`Lead related to bid: ${data.relateBidToLead.bid.name}`, {
          action: (
            <Button as="a" to={routePaths.bidDetail} params={{ id: data.relateBidToLead.bid.id }}>
              View Bid
            </Button>
          ),
        });
      } else {
        toast.success(`Bid relationship removed from lead`);
      }
      reset({}, { keepValues: true });
    },
    onError: (error) => {
      console.warn("Failed to relate bid to lead", JSON.stringify(error));
      toast.error("Failed to relate bid to lead");
    },
  });

  /**
   * Handles updates for simple fields by sending only the changed field.
   * This prevents race conditions where simultaneous updates could overwrite each other.
   */
  function handleUpdateFieldSubmit(fieldName: string, values: LeadUpdateFormOutput) {
    const fieldValue = values[fieldName as keyof LeadUpdateFormOutput];

    if (fieldValue === undefined) {
      return;
    }

    updateLeads({
      updateLeadsRequest: {
        ids: [lead.id],
        updateData: { [fieldName]: fieldValue },
      },
    });
  }

  /**
   * Handles updates for entity relationship fields (buyers, suppliers, frameworks).
   * These require diffing the current vs new values to determine which entities
   * to add or remove, rather than simply overwriting.
   */
  function handleEntityFieldSubmit(field: EntityField, values: LeadUpdateFormOutput) {
    switch (field) {
      case "buyerIds": {
        const customBuyerIdSet = new Set(lead.customBuyers.map((b) => b.id));
        const allCurrentBuyerIds = [
          ...lead.buyers.map((b) => b.id),
          ...lead.customBuyers.map((b) => b.id),
        ];
        const { added, removed } = diffArray(allCurrentBuyerIds, values.buyerIds);

        // Determine entity type per ID: custom buyer IDs use "custom_buyer", Stotles buyer IDs use "buyer"
        if (removed.length > 0) {
          removeLeadEntities(
            removed.map((id) => ({
              entityId: id,
              entityType: customBuyerIdSet.has(id) ? "custom_buyer" : "buyer",
            })),
          );
        }

        // Custom buyers are created and linked via their own mutation (useCreateCustomLeadOrganisation),
        // so only Stotles buyer IDs should be passed to addLeadEntities here.
        const stotlesAdded = added.filter((id) => !customBuyerIdSet.has(id));
        if (stotlesAdded.length > 0) {
          addLeadEntities(stotlesAdded.map((id) => ({ entityId: id, entityType: "buyer" })));
        }
        break;
      }

      case "supplierIds": {
        const { added, removed } = diffArray(
          lead.suppliers.map((s) => s.id),
          values.supplierIds,
        );

        if (removed.length > 0) {
          removeLeadEntities(removed.map((id) => ({ entityId: id, entityType: "supplier" })));
        }

        if (added.length > 0) {
          addLeadEntities(added.map((id) => ({ entityId: id, entityType: "supplier" })));
        }
        break;
      }

      case "frameworkId": {
        // Frameworks are single-select, so we handle add/remove/replace scenarios
        const currentFrameworkId = lead.frameworks.length > 0 ? lead.frameworks[0].id : null;
        const newFrameworkId = values.frameworkId;

        if (!newFrameworkId && currentFrameworkId) {
          // Clearing the framework selection
          removeLeadEntities([{ entityId: currentFrameworkId, entityType: "framework" }]);
        } else if (newFrameworkId && !currentFrameworkId) {
          // Setting a framework when none was previously selected
          addLeadEntities([{ entityId: newFrameworkId, entityType: "framework" }]);
        } else if (newFrameworkId && currentFrameworkId && newFrameworkId !== currentFrameworkId) {
          // Swapping to a different framework - must remove old before adding new
          removeLeadEntities([{ entityId: currentFrameworkId, entityType: "framework" }]);
          addLeadEntities([{ entityId: newFrameworkId, entityType: "framework" }]);
        }
        // If values match, no action needed
        break;
      }

      default:
        // Exhaustiveness check - TypeScript will error if a case is missed
        throw new Error(`Unhandled entity field: ${field}`);
    }
  }

  const useDebounce500 = createUseDebounce(500);

  /**
   * Debounced submit handler for use with useFormAutoSubmit.
   * Parses and validates form data, then routes to the appropriate handler
   * based on whether the changed field is an entity relationship or direct field.
   *
   * Note: The `value` field is explicitly skipped here as it uses blur-based submission.
   */
  const debouncedHandleSubmit = useDebounce500(
    (values: LeadUpdateFormInput, meta: { name: string }) => {
      // Value field uses blur submission to avoid API spam during typing
      if (meta.name === "value") return;

      // Parse through Zod to coerce types (e.g., date strings to ISO, value to number)
      const parsedValues = updateLeadSchema.parse(values);

      // This is handled in the parent component
      if (meta.name === "bidId") {
        relateBidToLead({
          req: { leadId: lead.id, bidId: parsedValues.bidId },
        });
        return;
      }

      if (isEntityField(meta.name)) {
        handleEntityFieldSubmit(meta.name, parsedValues);
      } else {
        handleUpdateFieldSubmit(meta.name, parsedValues);
      }
    },
  );

  /**
   * Blur-based submission handler specifically for the `value` field.
   * This prevents API calls on every keystroke while the user is typing a number.
   * Only submits if the field is valid and has been modified.
   */
  const submitValueUpdate = async () => {
    // Validate before submitting to avoid sending invalid data
    const isValid = await trigger("value");

    if (isValid && dirtyFields.value) {
      const parsedValues = updateLeadSchema.parse(getValues());
      // An empty input clears the value (null); z.coerce would otherwise turn "" into 0.
      // A 0 is only stored when the user explicitly types "0".
      const rawValue = getValues("value");
      if (rawValue === "" || rawValue == null) {
        parsedValues.value = null;
      }
      handleUpdateFieldSubmit("value", parsedValues);
    }
  };

  return {
    /** Debounced handler for auto-submit on field changes (excludes value field) */
    debouncedHandleSubmit,
    /** Blur handler specifically for the value field */
    submitValueUpdate,
    reset,
  };
}
